import { Link } from "react-router-dom";
import { Aperture, ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "June 25, 2026";
const COMPANY = "Chapter 8 Studio";
const CONTACT_EMAIL = "lawrence@chapter8studio.com";

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-2xl text-white mb-3">{title}</h2>
      <div className="space-y-3 text-neutral-300 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-white/10 bg-black/60 backdrop-blur-2xl">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="terms-logo" className="flex items-center gap-2.5">
            <Aperture className="h-6 w-6 text-amber-glow" strokeWidth={1.5} />
            <span className="font-display text-xl tracking-tight">Aevolux</span>
          </Link>
          <Link to="/login" data-testid="terms-back" className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="terms-content">
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-amber-glow mb-3">Legal</p>
        <h1 className="font-display text-5xl tracking-tight">Terms of Service</h1>
        <p className="text-neutral-500 mt-4">Effective date: {EFFECTIVE_DATE}</p>

        <p className="mt-8 text-neutral-300 leading-relaxed text-[15px]">
          These Terms of Service ("Terms") govern your access to and use of Aevolux (the "Service"),
          operated by {COMPANY} ("we", "us", "our"). By creating an account or using the Service, you
          agree to these Terms. If you do not agree, do not use the Service.
        </p>

        <Section title="1. Eligibility & Accounts">
          <p>You must be at least 13 years old (or the minimum age required in your jurisdiction) to use the Service. You are responsible for the information you provide, for keeping your password secure, and for all activity that occurs under your account. Notify us promptly of any unauthorized use.</p>
        </Section>

        <Section title="2. The Service">
          <p>Aevolux generates AI-assisted photography recommendations — including suggested camera settings, lighting setups, and composition ideas — based on the lens, lighting type, and number of lights you select. You may also save presets and review your generation history.</p>
        </Section>

        <Section title="3. AI-Generated Content Disclaimer">
          <p><strong className="text-white">Suggestions are advisory only.</strong> AI-generated recommendations are provided for guidance and inspiration. They may be inaccurate, incomplete, or unsuitable for your specific scene, gear, or conditions. You are solely responsible for the settings you choose and the results of any shoot. Always verify settings and exercise your own professional judgment, especially where safety or client work is involved.</p>
        </Section>

        <Section title="4. Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Use the Service for any unlawful purpose or in violation of these Terms.</li>
            <li>Attempt to disrupt, overload, reverse-engineer, or gain unauthorized access to the Service or its systems.</li>
            <li>Abuse, automate, or excessively consume the AI generation features beyond fair use or applicable plan limits.</li>
            <li>Resell, redistribute, or misrepresent the Service or its output as your own product.</li>
          </ul>
        </Section>

        <Section title="5. Subscriptions & Billing">
          <p>The Service may offer free and paid plans. Paid plans (where applicable) are billed in advance on a monthly or annual basis and renew automatically until cancelled. Fees are non-refundable except where required by law. We may change plan features or pricing with reasonable notice; changes will not affect the current paid term already in progress.</p>
        </Section>

        <Section title="6. Intellectual Property">
          <p>The Service, including its software, design, branding, and content (excluding your inputs and generated outputs), is owned by {COMPANY} and protected by applicable laws. We grant you a limited, non-exclusive, non-transferable license to use the Service for its intended purpose. You retain rights to the selections you submit; you may use the generated suggestions for your own photography work.</p>
        </Section>

        <Section title="7. Termination">
          <p>You may stop using the Service and request account deletion at any time. We may suspend or terminate your access if you violate these Terms or use the Service in a way that may cause harm or legal exposure. Upon termination, your right to use the Service ceases immediately.</p>
        </Section>

        <Section title="8. Disclaimer of Warranties">
          <p>The Service is provided "as is" and "as available" without warranties of any kind, whether express or implied, including fitness for a particular purpose, accuracy, or non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or that AI outputs will meet your expectations.</p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p>To the maximum extent permitted by law, {COMPANY} shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, data, or goodwill, arising from your use of (or inability to use) the Service or reliance on any AI-generated suggestion. Our total liability for any claim shall not exceed the amount you paid us for the Service in the 12 months preceding the claim.</p>
        </Section>

        <Section title="10. Governing Law">
          <p>These Terms and any dispute arising from your use of the Service are governed by the laws of the State of Illinois and, where applicable, the State of Arizona, without regard to conflict-of-law principles. You agree to the exclusive jurisdiction of the courts located in those states.</p>
        </Section>

        <Section title="11. Changes to These Terms">
          <p>We may update these Terms from time to time. Material changes will be reflected by updating the effective date above. Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms.</p>
        </Section>

        <Section title="12. Contact Us">
          <p>Questions about these Terms? Contact {COMPANY} at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="terms-contact-email" className="text-amber-glow hover:underline">{CONTACT_EMAIL}</a>.
          </p>
          <p className="text-neutral-500 text-sm pt-2">
            See also our <Link to="/privacy" className="text-amber-glow hover:underline">Privacy Policy</Link>.
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-white/10 text-sm text-neutral-500">
          © {new Date().getFullYear()} {COMPANY}. All rights reserved.
        </div>
      </div>
    </div>
  );
}
